// Render Activity board — operator view of live asset pipeline state.
// Answers "what is this asset doing / why did it stall" without SSH.
//
// Polls GET /api/ads/render-activity. Adaptive cadence: fast while any
// row is queued/rendering, slow when settled. Stalled rows (server flag:
// rendering + stageAgeSec > 600) are sorted first and filterable in one
// click. Diagnostics are copied verbatim from the server-built field.
//
// Status vocabulary matches models/Ad.js: queued | rendering | draft |
// live | archived | failed. A successful render lands on `draft`.
//
// Query identity: the fetcher returns an envelope that carries the key
// it was issued for. Display is a pure comparison — no receipt-time
// stamping, no dataQueryKey, no reachable spinner wedge.
//
// WHY this board once spun forever with ZERO network requests
// (known-open #13, live CDP on staging): usePoll's tick used to bail
// on document.visibilityState === 'hidden' even for the immediate arm
// after mount / enabled flip / Refresh. When visibility was stuck at
// 'hidden' (CDP session, occluded window, some DevTools setups),
// /api/ads/render-activity never left the browser — brand controls
// were enabled, Refresh issued nothing, no console errors. Fixed in
// usePoll (force-fetch on arm); this page also refuses to hide behind
// a permanent spinner when a load fails or never completes.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge, Box, Button, Card, CardBody, HStack, Input, Select, Spinner,
  Text, VStack, useToast
} from '@chakra-ui/react';
import { Copy, RefreshCw } from 'lucide-react';
import { PageHeader } from '../../shell/PageHeader';
import { usePoll } from '../../shell/usePoll';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import { RenderActivityRow } from './RenderActivityRow';
import { copyText, formatRelativeIso, joinDiagnostics } from './format';
import type {
  LimitOption,
  RenderActivityEnvelope,
  RenderActivityResponse,
  StatusFilter
} from './types';

const POLL_ACTIVE_MS = 4000;
const POLL_IDLE_MS = 20000;
// If we still have no matching envelope after this, stop spinning and
// say so — a permanent spinner hid #13 for days. Deliberately generous:
// this is a backstop for a board that will NEVER load, and a genuinely
// slow response flashing "Couldn't load" on an ops board is its own bug.
// The dead case stays dead, so a longer wait still catches it.
const LOAD_TIMEOUT_MS = 30000;

export function RenderActivityPage() {
  const { activeBrand, loading: brandLoading } = useBrand();
  const brandId = activeBrand?.id || null;
  const toast = useToast();

  const [status, setStatus] = useState<StatusFilter>('');
  const [runId, setRunId] = useState('');
  const [runIdApplied, setRunIdApplied] = useState('');
  const [limit, setLimit] = useState<LimitOption>(50);
  // Client-only — server has no stalled= filter; stalled is a derived
  // flag on rendering rows. One click surfaces them without re-query.
  const [stalledOnly, setStalledOnly] = useState(false);
  // Load-timeout state — if no matching envelope lands, stop spinning
  // and show an explicit failure (hid #13 for days). Stores the KEY the
  // timeout fired for, not a bare boolean: a timeout recorded for brand A
  // must not paint an error over brand B on the render before the effect
  // re-arms.
  const [timedOutKey, setTimedOutKey] = useState<string | null>(null);
  // Bumped on explicit Refresh/Retry so the timeout effect re-arms even
  // when queryKey and current are unchanged.
  const [retryNonce, setRetryNonce] = useState(0);

  // Full query identity. The fetcher captures this into the envelope so
  // display can gate without any effect that stamps at receipt time.
  const queryKey = useMemo(
    () => `${brandId ?? ''}|${status}|${runIdApplied}|${limit}`,
    [brandId, status, runIdApplied, limit]
  );

  // Track which key we last armed a request for — prevents double-fetch
  // on mount/hydrate while still refreshing on genuine key changes.
  const lastArmedKeyRef = useRef<string | null>(null);

  const fetcher = useCallback(async (): Promise<RenderActivityEnvelope> => {
    // Capture the key THIS request is for before any await.
    const key = queryKey;
    try {
      if (!brandId) {
        return { key, ok: false, message: 'brandId required' };
      }
      const qp = new URLSearchParams({
        brandId,
        limit: String(limit)
      });
      if (status) qp.set('status', status);
      if (runIdApplied.trim()) qp.set('runId', runIdApplied.trim());
      const payload = await apiJson<RenderActivityResponse>(
        `/api/ads/render-activity?${qp}`
      );
      return { key, ok: true, payload };
    } catch (err) {
      return {
        key,
        ok: false,
        message: err instanceof Error ? err.message : 'Request failed'
      };
    }
  }, [queryKey, brandId, limit, status, runIdApplied]);

  // Envelope never throws (failures are ok:false), so usePoll's error is
  // only set if the fetcher itself rejects — still surface it so a
  // silent path can never strand the board on a spinner again.
  const { data, error: pollError, refresh } = usePoll(fetcher, {
    enabled: !!brandId,
    interval: (d) => {
      if (!d || !d.ok) return POLL_IDLE_MS;
      const rows = d.payload.rows || [];
      const busy = rows.some((r) => r.status === 'rendering' || r.status === 'queued');
      return busy ? POLL_ACTIVE_MS : POLL_IDLE_MS;
    }
  });

  // Arm on genuine key change. First brand-keyed value is recorded only —
  // usePoll force-ticks when enabled flips true (see usePoll force flag).
  useEffect(() => {
    if (!brandId) return;
    if (queryKey === lastArmedKeyRef.current) return;
    if (lastArmedKeyRef.current === null) {
      lastArmedKeyRef.current = queryKey;
      return;
    }
    lastArmedKeyRef.current = queryKey;
    refresh();
  }, [brandId, queryKey, refresh]);

  // Pure gate — non-matching envelope is treated as "no data yet".
  // Do NOT drop the key comparison: it prevents brand A's rows showing
  // after a switch to brand B (the envelope exists for that reason).
  const current = data && data.key === queryKey ? data : null;

  const rows = current?.ok ? current.payload.rows : [];
  const count = current?.ok ? current.payload.count : null;
  const generatedAt = current?.ok ? current.payload.generatedAt : null;

  // Reachable failure states — never leave the operator on a bare spinner.
  // Priority: envelope ok:false (matched key) > usePoll throw > load timeout.
  useEffect(() => {
    if (!brandId || current != null) {
      setTimedOutKey(null);
      return;
    }
    setTimedOutKey(null);
    const t = setTimeout(() => setTimedOutKey(queryKey), LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [brandId, current, queryKey, retryNonce]);

  const handleRefresh = useCallback(() => {
    setTimedOutKey(null);
    setRetryNonce((n) => n + 1);
    refresh();
  }, [refresh]);

  // Only the CURRENT query's timeout counts — see timedOutKey.
  const loadTimedOut = timedOutKey === queryKey;

  const displayError =
    (current && !current.ok ? current.message : null)
    || pollError
    || (loadTimedOut && current == null
      ? 'Timed out waiting for render activity. Click Refresh to retry.'
      : null);

  // Page-scoped only — server returns the `limit` most-recently-updated
  // rows, so older stalled ads can fall off this page.
  const stalledCount = useMemo(
    () => rows.filter((r) => r.stalled).length,
    [rows]
  );

  // Stalled first, then in-flight, then the rest — operators open this
  // page to find the fire, not chronological noise.
  const displayRows = useMemo(() => {
    const filtered = stalledOnly ? rows.filter((r) => r.stalled) : rows;
    return [...filtered].sort((a, b) => {
      if (a.stalled !== b.stalled) return a.stalled ? -1 : 1;
      const aBusy = a.status === 'rendering' || a.status === 'queued' ? 0 : 1;
      const bBusy = b.status === 'rendering' || b.status === 'queued' ? 0 : 1;
      if (aBusy !== bBusy) return aBusy - bBusy;
      return 0;
    });
  }, [rows, stalledOnly]);

  const applyRunId = () => setRunIdApplied(runId.trim());

  // Copy-all reads only from `current` (via displayRows derived from it)
  // so it can never paste another brand's diagnostics.
  const copyAll = async () => {
    if (!current?.ok) {
      toast({ title: 'Nothing to copy', status: 'warning', duration: 2000 });
      return;
    }
    const blocks = displayRows
      .map((r) => r.diagnostic ?? '')
      .filter((d) => d.length > 0);
    const text = joinDiagnostics(blocks);
    if (!text) {
      toast({ title: 'Nothing to copy', status: 'warning', duration: 2000 });
      return;
    }
    try {
      await copyText(text);
      toast({
        title: `Copied ${blocks.length} diagnostic${blocks.length === 1 ? '' : 's'}`,
        status: 'success',
        duration: 2000
      });
    } catch (e) {
      toast({
        title: 'Copy failed',
        description: e instanceof Error ? e.message : 'Clipboard unavailable',
        status: 'error',
        duration: 4000
      });
    }
  };

  const hasOkData = current?.ok === true;
  // BrandContext still hydrating — activeBrand is null even when
  // localStorage.brand_id is set; show spinner, not "No brand selected".
  // No matching envelope (including mid-flight key change) → loading,
  // but never over a visible error (timeout / ok:false / poll throw).
  const loading =
    brandLoading || (!!brandId && current == null && !displayError);

  return (
    <VStack align="stretch" spacing={6}>
      <PageHeader
        eyebrow="Ops"
        title="Render Activity"
        description="Live asset pipeline for this brand — stage, stall age, model, and a paste-ready diagnostic for every row. Stalled renders surface first."
        primaryAction={
          <Button
            size="sm"
            variant="outline"
            leftIcon={<RefreshCw size={14} />}
            onClick={handleRefresh}
            isDisabled={!brandId}
          >
            Refresh
          </Button>
        }
        secondaryAction={
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Copy size={14} />}
            onClick={() => void copyAll()}
            isDisabled={!hasOkData || displayRows.length === 0}
          >
            Copy all
          </Button>
        }
      />

      {/* Stalled callout — only when current envelope is ok (never beside an error). */}
      {brandId && hasOkData && stalledCount > 0 && (
        <Card
          variant="outline"
          borderColor="red.400"
          borderWidth="2px"
          bg="red.50"
          cursor="pointer"
          onClick={() => setStalledOnly(true)}
        >
          <CardBody py={3} px={4}>
            <HStack justify="space-between" align="center" flexWrap="wrap" spacing={3}>
              <HStack spacing={3}>
                <Badge colorScheme="red" fontSize="12px" px={2} py={1}>
                  {stalledCount} stalled
                </Badge>
                <Text fontSize="sm" fontWeight="600" color="red.800">
                  In the {limit} most recently updated
                  {stalledCount === 1 ? ' row' : ' rows'}: rendering past 10
                  minutes on the current stage. Click to filter.
                </Text>
              </HStack>
              {stalledOnly && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStalledOnly(false);
                  }}
                >
                  Clear stalled filter
                </Button>
              )}
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* Filters — status / runId / limit hit the server; stalled is client-only. */}
      <Card variant="outline" borderColor="brand.border">
        <CardBody py={3} px={4}>
          <HStack spacing={3} align="center" flexWrap="wrap">
            <Select
              size="sm"
              width={{ base: '100%', md: '150px' }}
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
              isDisabled={!brandId}
            >
              <option value="">All statuses</option>
              <option value="queued">Queued</option>
              <option value="rendering">Rendering</option>
              <option value="draft">Draft</option>
              <option value="live">Live</option>
              <option value="archived">Archived</option>
              <option value="failed">Failed</option>
            </Select>

            <HStack spacing={1} flex={{ base: '1 1 100%', md: '0 1 280px' }}>
              <Input
                size="sm"
                placeholder="runId filter"
                value={runId}
                onChange={(e) => setRunId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyRunId();
                }}
                isDisabled={!brandId}
                fontFamily="mono"
                fontSize="12px"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={applyRunId}
                isDisabled={!brandId}
              >
                Apply
              </Button>
              {runIdApplied && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setRunId('');
                    setRunIdApplied('');
                  }}
                >
                  Clear
                </Button>
              )}
            </HStack>

            <Select
              size="sm"
              width="100px"
              value={String(limit)}
              onChange={(e) => setLimit(Number(e.target.value) as LimitOption)}
              isDisabled={!brandId}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </Select>

            <Button
              size="sm"
              variant={stalledOnly ? 'solid' : 'outline'}
              colorScheme={stalledOnly ? 'red' : 'gray'}
              onClick={() => setStalledOnly((v) => !v)}
              isDisabled={!brandId}
              title="Stalled among rows on this page only (most recently updated)"
            >
              Stalled{hasOkData && stalledCount > 0 ? ` (${stalledCount})` : ''}
            </Button>

            <Box flex={1} />

            {generatedAt && (
              <Text fontSize="11px" color="brand.muted" whiteSpace="nowrap">
                Updated {formatRelativeIso(generatedAt)}
              </Text>
            )}
            {hasOkData && count != null && (
              <Text fontSize="11px" color="brand.muted" whiteSpace="nowrap">
                {stalledOnly || status || runIdApplied
                  ? `${displayRows.length} shown · ${count} returned`
                  : `${count} returned`}
                {` · limit ${limit}`}
              </Text>
            )}
          </HStack>
        </CardBody>
      </Card>

      {brandLoading && (
        <Box textAlign="center" py={6}>
          <Spinner size="md" />
        </Box>
      )}

      {!brandLoading && !brandId && (
        <Card variant="outline">
          <CardBody>
            <VStack align="stretch" spacing={2} py={8} textAlign="center">
              <Text
                fontSize="sm"
                color="brand.muted"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="0.06em"
              >
                No brand selected
              </Text>
              <Text color="brand.ink" fontWeight="600" fontSize="lg">
                Pick a brand in the switcher to load render activity.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      )}

      {!brandLoading && loading && brandId && (
        <Box textAlign="center" py={6}>
          <Spinner size="md" />
        </Box>
      )}

      {displayError && brandId && (
        <Card variant="outline" borderColor="red.300">
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Text color="red.600" fontSize="sm" fontWeight="600">
                Couldn&apos;t load render activity
              </Text>
              <Text color="red.500" fontSize="sm">
                {displayError}
              </Text>
              <Box>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  leftIcon={<RefreshCw size={14} />}
                  onClick={handleRefresh}
                >
                  Retry
                </Button>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      )}

      {!loading && !displayError && brandId && hasOkData && displayRows.length === 0 && (
        <Card variant="outline">
          <CardBody>
            <VStack align="stretch" spacing={2} py={8} textAlign="center">
              <Text
                fontSize="sm"
                color="brand.muted"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="0.06em"
              >
                {stalledOnly
                  ? 'No stalled assets'
                  : status || runIdApplied
                    ? 'No matching assets'
                    : 'No render activity'}
              </Text>
              <Text color="brand.ink" fontWeight="600" fontSize="lg">
                {stalledOnly
                  ? `Nothing in the ${limit} most recently updated is past the 10-minute stage threshold.`
                  : status || runIdApplied
                    ? 'Try clearing filters or widening the limit.'
                    : 'No recent asset pipeline rows for this brand.'}
              </Text>
            </VStack>
          </CardBody>
        </Card>
      )}

      {!loading && !displayError && brandId && displayRows.length > 0 && (
        <Card variant="outline" borderColor="brand.border">
          <CardBody p={0}>
            <HStack
              px={4}
              py={2}
              spacing={3}
              fontSize="11px"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.06em"
              color="brand.muted"
              borderBottomWidth="1px"
              borderColor="brand.border"
              display={{ base: 'none', md: 'flex' }}
            >
              <Box w="16px" />
              <Box flex={1}>Status · stage · meta</Box>
              <Box w="72px" textAlign="right">Copy</Box>
            </HStack>
            <VStack
              align="stretch"
              spacing={0}
              divider={<Box borderTopWidth="1px" borderColor="brand.border" />}
            >
              {displayRows.map((row) => (
                <RenderActivityRow key={row.assetId} row={row} />
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}
